// George Mason University: public college page (#2739), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - George Mason's Common Data Set 2025-2026 (Office of Institutional
//    Effectiveness and Planning): C1 applicants, admits and enrollees for
//    Fall 2025 with the residency table (in-state and out-of-state are
//    filled; the international column is blank, so it is left out), C8 test
//    policy for Fall 2027 and the score date, C21/C22 plans (no early
//    decision; early action with dates but no counts). Its G1 and G5 cost
//    tables are blank, so the cost comes from the university's 2026-27 Cost
//    of Attendance page.
//  - gmu.edu freshman admissions: Dates and Deadlines, Freshman Application
//    Status (decision timing), Score Optional Consideration, and Selective
//    Majors; the College of Visual and Performing Arts' Undergraduate
//    Admissions page for audition and portfolio dates.
//  - College Scorecard API, school id 232186 (net price by family income).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'George Mason Common Data Set 2025-2026',
        url: 'https://oiep.gmu.edu/wp-content/uploads/2026/02/CDS-PDF-2025-2026_PDF_Template_Final_02_19.pdf',
        read: '2026-10-01',
    },
    dates: {
        label: 'George Mason Freshman Admissions, Dates and Deadlines',
        url: 'https://www.gmu.edu/freshman/dates-deadlines',
        read: '2026-10-01',
    },
    status: {
        label: 'George Mason Freshman Admissions, Freshman Application Status',
        url: 'https://www.gmu.edu/freshman/application-status',
        read: '2026-10-01',
    },
    score: {
        label: 'George Mason Freshman Admissions, Score Optional Consideration',
        url: 'https://www.gmu.edu/freshman/requirements/score-optional',
        read: '2026-10-01',
    },
    selective: {
        label: 'George Mason Freshman Admissions, Selective Majors',
        url: 'https://www.gmu.edu/freshman/requirements/selective-majors',
        read: '2026-10-01',
    },
    cvpa: {
        label: 'George Mason College of Visual and Performing Arts, Undergraduate Admissions',
        url: 'https://cvpa.gmu.edu/admissions/undergraduate-admissions',
        read: '2026-10-01',
    },
    coa: {
        label: 'George Mason Admissions and Aid, 2026-27 Cost of Attendance',
        url: 'https://www.gmu.edu/admissions-aid/cost-attendance',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?232186-George-Mason-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'george-mason-university',
    kbId: 'george_mason_university',
    name: 'George Mason University',
    shortName: 'George Mason',
    aliases: ['Mason', 'George Mason'],
    location: 'Fairfax, VA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Mason brand guidelines); decoration only.
    branding: {
        primary: '#005239',
        secondary: '#FFC733',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/george_mason_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 26014,
        admitted: 22495,
        enrolled: 4337,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 16236, admitted: 14222 },
            { group: 'Out-of-state', applied: 9778, admitted: 8273 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'George Mason\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'George Mason has a nonbinding early action plan and no early decision plan. The Common Data Set gives no early action counts, so this page shows no early action acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Admission to George Mason and to your major usually come together. Some majors ask for more:',
        rows: [
            { school: 'Computer science and engineering (College of Engineering and Computing)', requirement: 'For direct admission you need four years of high school math, including one course beyond Algebra II, plus either a math SAT of 550 or math ACT of 24, or grades of B or higher (not B-) in every math course through Algebra II. Admitted students who miss these start in CEC Undeclared. Information Technology and Statistics have no extra requirement.', source: 'selective' },
            { school: 'Nursing (BSN)', requirement: 'The School of Nursing reviews freshman applications a second time for direct admission, and admission is highly competitive. Students admitted to the university but not to Nursing start as undeclared majors.', source: 'selective' },
            { school: 'College of Visual and Performing Arts', requirement: 'Every program except the School of Art needs an audition, portfolio review or interview. Film and Computer Game Design portfolios are due by November 7 for early action and February 1 for regular decision; Dance, Music and Theater auditions must be scheduled at least two weeks before the audition date.', source: 'cvpa' },
        ],
        source: 'selective',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'February 1' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid-December', binding: false },
            { plan: 'Regular Decision', deadline: 'February 1', decision: 'In the spring', binding: false },
        ],
        note: 'Applications that come in after these deadlines are reviewed on a space-available basis. All decisions are posted in the George Mason Admissions Portal.',
        source: 'status',
        binding: 'Early action is nonbinding. Applying by November 1 gives you priority consideration for the Honors College, University Scholars and merit scholarships; regular decision applicants are considered for the Honors College on a space-available basis. The FAFSA or VASA priority date for state and university grants is March 1.',
        bindingSource: 'dates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'George Mason has been score optional since 2007: you choose whether to send SAT or ACT scores, and students who apply without them are not penalized. Score optional applicants to computer science and engineering majors need four years of high school math, including a course beyond Algebra II, all with grades of B or better. NCAA Division I recruits can also apply without scores.',
        source: 'score',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants. If you apply with scores, you self-report them, and your admission then depends on official scores arriving before classes start (by August 15 for fall).',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [14807, 39803] },
            { item: 'Food and housing (on campus)', amounts: [15020, 15020] },
            { item: 'Books', amounts: [1380, 1380] },
            { item: 'Transportation', amounts: [1380, 1380] },
            { item: 'Personal expenses', amounts: [2384, 2384] },
        ],
        totals: [34971, 59967],
        note: 'The university\'s estimated budget for a full-time undergraduate taking 12 to 15 semester hours and living on campus; George Mason gives the items, not a total. Food and housing is the average of the Traditional Double and Suite Double rooms. New freshmen also pay a one-time $350 new student fee, which is not in the totals, and some courses carry extra course or lab fees.',
        source: 'coa',
        netPrice: {
            average: 14697,
            byIncome: [
                { income: '$0 to $30,000', amount: 9594 },
                { income: '$30,001 to $48,000', amount: 9467 },
                { income: '$48,001 to $75,000', amount: 11341 },
                { income: '$75,001 to $110,000', amount: 16147 },
                { income: 'Over $110,000', amount: 23862 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from George Mason\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
